import { Actions, Events } from "@events";
import axios from "axios";
import { EventEmitter } from "events";

const DEFAULT_RECONNECT_CONFIG = {
  maxReconnectAttempts: 5,
  initialReconnectDelay: 100, // ms — first attempt should be near-instant to beat server grace period
  reconnectDelayFactor: 3, // Exponential backoff factor
  maxReconnectDelay: 30000, // ms
  connectionTimeoutMs: 10000, // ms, timeout for a single WebSocket connection attempt
};

class Signaling extends EventEmitter {
  /**
   * @type {import('./webrtcRoom').default}
   */
  webRtcRoom = null;
  /** @type {WebSocket} */
  websocket = null;
  rtcId = null;
  clientId = null;
  wssUrl = "";
  registered = false;
  _pendingMessages = [];

  _isReconnecting = false;
  _reconnectAttempts = 0;
  _reconnectTimeoutId = null;
  _manuallyDisconnected = false;
  _config = DEFAULT_RECONNECT_CONFIG;

  constructor(webRtcRoom, config = {}) {
    super();

    this.webRtcRoom = webRtcRoom;
    this.rtcId = webRtcRoom.rtcId;
    this.clientId = webRtcRoom.clientId;
    this._config = { ...DEFAULT_RECONNECT_CONFIG, ...config };

    let apiUrl = axios.defaults.baseURL;
    let socketProtocol = apiUrl.startsWith("https") ? "wss" : "ws";
    let host = new URL(apiUrl).host;
    this.wssUrl = socketProtocol + "://" + host + "/ws";

    this.webRtcRoom.localActionEmitter.on(Actions.SendSignalingMessage, (message) => {
      this.send(message);
    });
  }

  async startSignaling() {
    if (this.websocket && (this.websocket.readyState === WebSocket.OPEN || this.websocket.readyState === WebSocket.CONNECTING)) {
      console.log("WebSocket connection already open or connecting.");
      if (this.websocket.readyState === WebSocket.OPEN && !this.registered) {
        try {
          await this.signalingRegister();
        } catch (regError) {
          console.error("Failed to re-register on existing open connection:", regError);
        }
      }
      return Promise.resolve();
    }

    if (this.websocket) {
      this.websocket.onopen = null;
      this.websocket.onmessage = null;
      this.websocket.onclose = null;
      this.websocket.onerror = null;
      if (this.websocket.readyState !== WebSocket.CLOSED) {
        this.websocket.close();
      }
    }
    this.websocket = null;

    this._manuallyDisconnected = false;
    this.emit("connecting");
    console.log(`Attempting to connect to ${this.wssUrl}`);
    this.websocket = new WebSocket(this.wssUrl);

    this.websocket.onmessage = (event) => this.processSignalingMessage(event);

    this.websocket.onerror = (event) => {
      console.error("WebSocket error event:", event);
      // Errors leading to connection failure are handled by the promise's reject or onclose.
    };

    this.websocket.onclose = (event) => {
      const reason = event.reason || "No reason provided";
      const code = event.code || "N/A";
      console.log(`WebSocket closed. Code: ${code}, Reason: "${reason}"`);
      this.registered = false;
      this._handleDisconnection(event);
    };

    return new Promise((resolve, reject) => {
      let promiseSettled = false;

      const clearPromiseHandlers = () => {
        if (this.websocket) {
          this.websocket.removeEventListener("open", onOpenPromiseHandler);
          this.websocket.removeEventListener("error", onErrorPromiseHandler);
        }
      };

      const onOpenPromiseHandler = () => {
        if (promiseSettled) return;
        promiseSettled = true;
        clearTimeout(connectTimeoutId);
        clearPromiseHandlers();

        this._isReconnecting = false;
        this._reconnectAttempts = 0;
        this.emit("connected");
        console.log("Websocket connected");
        resolve();
      };

      const onErrorPromiseHandler = (event) => {
        if (promiseSettled) return;
        promiseSettled = true;
        clearTimeout(connectTimeoutId);
        clearPromiseHandlers();
        console.error("WebSocket connection attempt failed (error event):", event);
        reject(new Error("WebSocket connection failed due to an error."));
      };

      const connectTimeoutId = setTimeout(() => {
        if (promiseSettled) return;
        if (this.websocket && this.websocket.readyState === WebSocket.CONNECTING) {
          promiseSettled = true;
          clearPromiseHandlers();
          console.warn("WebSocket connection attempt timed out.");
          this.websocket.close(1000, "ConnectionTimeout"); // Triggers persistent onclose
          reject(new Error("WebSocket connection timed out."));
        }
      }, this._config.connectionTimeoutMs);

      this.websocket.addEventListener("open", onOpenPromiseHandler, { once: true });
      this.websocket.addEventListener("error", onErrorPromiseHandler, { once: true });
      // The persistent 'onclose' handles cases where the socket closes before 'open' or 'error' during connection.
    });
  }

  _handleDisconnection(event) {
    this.emit("disconnected", { code: event.code, reason: event.reason });

    if (this._manuallyDisconnected) {
      console.log("WebSocket disconnected manually, no auto-reconnection.");
      this._isReconnecting = false;
      return;
    }

    if (this._isReconnecting) {
      // Reconnection process already active
      return;
    }

    console.log("WebSocket disconnected, initiating reconnection sequence...");
    this._isReconnecting = true;
    this._reconnectAttempts = 0;
    this._scheduleReconnect();
  }

  _scheduleReconnect() {
    if (this._manuallyDisconnected) {
      this._isReconnecting = false;
      return;
    }

    this._reconnectAttempts++;

    if (this._reconnectAttempts > this._config.maxReconnectAttempts) {
      console.error("Max reconnection attempts reached. Giving up.");
      this.emit("reconnectionFailed");
      this._isReconnecting = false;
      this._reconnectAttempts = 0;
      return;
    }

    let delay = this._config.initialReconnectDelay * Math.pow(this._config.reconnectDelayFactor, this._reconnectAttempts - 1);
    delay = Math.min(delay, this._config.maxReconnectDelay);

    this.emit("reconnecting", { attempt: this._reconnectAttempts, delay: delay });
    console.log(`Scheduling reconnection attempt ${this._reconnectAttempts} in ${delay / 1000}s.`);

    if (this._reconnectTimeoutId) {
      clearTimeout(this._reconnectTimeoutId);
    }

    this._reconnectTimeoutId = setTimeout(async () => {
      if (this._manuallyDisconnected) {
        this._isReconnecting = false;
        console.log("Reconnection cancelled due to manual disconnect.");
        return;
      }
      await this._tryReconnect();
    }, delay);
  }

  async _tryReconnect() {
    if (this._manuallyDisconnected) {
      this._isReconnecting = false;
      return;
    }
    console.log(`Attempting reconnection (attempt ${this._reconnectAttempts} of ${this._config.maxReconnectAttempts})...`);
    try {
      await this.startSignaling(); // Sets _isReconnecting=false, _reconnectAttempts=0 on success
      await this.signalingRegister();
      this.emit("reconnected");
      console.log("Successfully reconnected and re-registered.");
    } catch (error) {
      console.error(`Reconnect attempt ${this._reconnectAttempts} failed: ${error.message}`);
      // If startSignaling failed to open, _isReconnecting should still be true.
      // Continue scheduling reconnections if still in that state and not manually disconnected.
      if (this._isReconnecting && !this._manuallyDisconnected) {
        this._scheduleReconnect();
      } else if (this._manuallyDisconnected) {
        console.log("Reconnection attempt aborted due to manual disconnection.");
        this._isReconnecting = false;
      }
    }
  }

  disconnect() {
    console.log("Manual disconnect called.");
    this._manuallyDisconnected = true;
    this._isReconnecting = false;
    this.registered = false;

    if (this._reconnectTimeoutId) {
      clearTimeout(this._reconnectTimeoutId);
      this._reconnectTimeoutId = null;
    }

    if (this.websocket) {
      if (this.websocket.readyState === WebSocket.OPEN || this.websocket.readyState === WebSocket.CONNECTING) {
        this.websocket.close(1000, "Client initiated disconnect"); // 1000: normal closure
      }
      this.websocket = null;
    }
    this.emit("disconnected", { code: 1000, reason: "Client initiated disconnect" });
  }

  async processSignalingMessage(messageEvent) {
    // console.log("Websocket message: " + messageEvent.data); // Can be too verbose for production
    if (messageEvent.data.indexOf("heartbeat") !== -1) {
      return;
    }

    let messageData;
    try {
      messageData = JSON.parse(messageEvent.data);
    } catch (e) {
      console.error("Failed to parse signaling message:", e, messageEvent.data);
      return;
    }

    // Handle potential double-wrapping or 'msg' field
    if (messageData.msg && typeof messageData.msg === "object") {
      messageData = messageData.msg;
    } else if (messageData.msg && typeof messageData.msg === "string") {
      try {
        messageData = JSON.parse(messageData.msg);
      } catch (e) {
        console.error("Failed to parse inner 'msg' field:", e, messageData.msg);
      }
    }

    if (typeof messageData === "string") {
      try {
        messageData = JSON.parse(messageData);
      } catch (e) {
        console.error("Failed to parse final message data string:", e, messageData);
        return;
      }
    }

    this.emit("message", messageData);
    this.webRtcRoom.callEventEmitter.emit(Events.SignalingMessageReceived, messageData);
  }

  async signalingRegister() {
    if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
      console.warn("Cannot register: WebSocket not open.");
      throw new Error("WebSocket not open. Cannot send registration message.");
    }

    var registerMessage = {
      cmd: SignalingMessageTypes.Register,
      roomid: this.rtcId,
      clientid: this.clientId,
    };

    if (this.webRtcRoom.state.role == "agent") registerMessage.isAgent = true;

    console.log("Sending register message: " + JSON.stringify(registerMessage));
    await this.send(registerMessage);

    this.registered = true;
    this.emit("registered");
    this.webRtcRoom.callEventEmitter.emit(Events.SignalingRegistered);
    console.log("Signaling registered successfully.");

    // Send any pending messages that were queued before registration
    while (this._pendingMessages.length > 0) {
      const pendingMessage = this._pendingMessages.shift();
      await this.send(pendingMessage);
    }
    console.log("All pending messages sent after registration.");
  }

  async send(message) {
    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
      // if not yet registered, only allow sending register messages and store the rest for later sending after registration
      if (!this.registered && message.cmd !== SignalingMessageTypes.Register) {
        this._pendingMessages.push(message);
        return;
      }

      try {
        this.websocket.send(JSON.stringify(message));
        this.webRtcRoom.callEventEmitter.emit(Events.SignalingMessageSent, message);
      } catch (error) {
        console.error("Error sending signaling message:", error, message);
        throw error;
      }
    } else {
      console.warn("WebSocket not ready, cannot send message:", message);
      this._pendingMessages.push(message);
    }
  }

  async sendWrapped(msg) {
    return this.send({
      cmd: SignalingMessageTypes.Send,
      msg: JSON.stringify(msg),
    });
  }
}

const SignalingMessageTypes = {
  Bye: "bye",
  Send: "send",
  Register: "register",
  Candidate: "candidate",
  Offer: "offer",
  Answer: "answer",
  TextChat: "textChat",
};

export { Signaling, SignalingMessageTypes };
